import { addObjectIdToDefsId, getVariablesFromSmartObject } from ".";
import { getSmartObjectName, getSmartObjectViewBox } from "./utils";
import { ObjectActionsType } from "../../contexts/ObjectsProvider";
import { SelectedObjectActionTypes } from "../../contexts/SelectedObjectProvider/SelectedObjectProvider";
import { SmartObject } from "../../types";

export type AddSmartObjectToPageOptions = {
  blobPath: string;
  assetVersionId: number;
  selectedObject: SmartObject;
  viewport: { width?: number; height?: number };
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  addNewMetaVariable: (variable: any) => void;
  clearObjectMetaVariables: (objectId: string) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  objectsDispatch: (action: any) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  selectedObjectDispatch: (action: any) => void;
};

/**
 * Adds a SmartObject to the page using only the asset blobPath and assetVersionId.
 * This encapsulates the logic previously in AssetManagerWrapper and can be reused
 * right after selecting a file or when hydrating an incomplete SmartObject on page open.
 */
export async function addSmartObjectToPage(options: AddSmartObjectToPageOptions) {
  const {
    blobPath,
    assetVersionId,
    selectedObject,
    viewport,
    addNewMetaVariable,
    clearObjectMetaVariables,
    objectsDispatch,
    selectedObjectDispatch,
  } = options;

  // Basic validation
  if (!blobPath) return;
  if (!viewport.width || !viewport.height) return;

  // Derive paths and fetch SmartObject definition
  // Support both styles of blobPath:
  //  - path/to/AssetName (folder only)
  //  - path/to/AssetName.svg (file included)
  const pathParts = blobPath.split("/");
  const lastPart = pathParts[pathParts.length - 1] || "";
  const hasSvgFile = /\.svg$/i.test(lastPart);
  const folderPath = hasSvgFile ? pathParts.slice(0, -1).join("/") : blobPath;
  const fileNameFromBlob = hasSvgFile ? lastPart : undefined;

  // Always use folderPath to fetch smartobject.json (whether blobPath had .svg or not)
  const smartObjectPath = `/api/Asset/${folderPath}/smartobject.json`;
  const res = await fetch(smartObjectPath);
  if (!res.ok) return; // Could enhance error handling/logging if needed
  const smartObjectRes = await res.json();

  const smartObject = addObjectIdToDefsId(smartObjectRes, selectedObject.objectId);

  // If editing an existing SmartObject, clear its current meta variables first
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  if ((selectedObject as any).elementList) {
    clearObjectMetaVariables(String(selectedObject.objectId));
  }

  const name = getSmartObjectName(smartObject[0]);
  // Prefer the original filename from the blobPath when present; otherwise, derive from SmartObject name
  const prettyBlobPath = fileNameFromBlob ?? `${name}.svg`;

  const { variables, variablesData, fonts } = getVariablesFromSmartObject(smartObject[0], selectedObject.objectId);

  const viewBox = getSmartObjectViewBox(smartObject[0]);
  if (!viewBox) return;

  const viewBoxX = viewBox[0];
  const viewBoxY = viewBox[1];
  const viewBoxWidth = viewBox[2];
  const viewBoxHeight = viewBox[3];

  const aspectRatio = viewBoxWidth / viewBoxHeight;
  const isPortrait = aspectRatio < 1;

  // Desired starting size as percentage of viewport large side
  const desiredStartingWidthPercent = 60;
  const desiredStartingHeightPercent = 60;
  const desiredStartingWidth = (viewport.width * desiredStartingWidthPercent) / 100;
  const desiredStartingHeight = (viewport.height * desiredStartingHeightPercent) / 100;

  let newWidth = viewBoxWidth;
  let newHeight = viewBoxHeight;
  if (isPortrait) {
    newHeight = desiredStartingHeight;
    newWidth = (newHeight / viewBoxHeight) * viewBoxWidth;
  } else {
    newWidth = desiredStartingWidth;
    newHeight = (newWidth / viewBoxWidth) * viewBoxHeight;
  }

  const widthPercent = (newWidth / viewport.width) * 100;
  const heightPercent = (newHeight / viewport.height) * 100;

  // Register variables and fonts as meta variables
  Object.keys(variables).forEach((key) => {
    addNewMetaVariable({
      ...variablesData[key],
      key,
      value: variables[key],
    });
  });

  if (fonts) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const fontsMap = fonts as Record<string, any>;
    Object.keys(fontsMap).forEach((key) => {
      const fontVal = fontsMap[key];
      addNewMetaVariable({
        ...(fontVal || {}),
        key,
        value: fontVal,
      });
    });
  }

  const originalViewBox = `${viewBoxX} ${viewBoxY} ${viewBoxWidth} ${viewBoxHeight}`;

  // Update the object on the canvas
  objectsDispatch({
    type: ObjectActionsType.UPDATE_OBJECT,
    payload: {
      objectId: selectedObject.objectId,
      object: {
        // Keep original asset path for future hydration
        assetBlobPath: blobPath,
        blobPath: prettyBlobPath,
        displayName: name ? name : selectedObject.displayName,
        elementList: smartObject,
        width: widthPercent,
        height: heightPercent,
        viewBox: originalViewBox,
        viewBoxZoom: 0,
        viewBoxX,
        viewBoxY,
        assetVersionId,
      },
    },
  });

  // Also update the selected object pixel dimensions in the selection store
  selectedObjectDispatch({
    type: SelectedObjectActionTypes.SET_WIDTH,
    payload: newWidth,
  });
  selectedObjectDispatch({
    type: SelectedObjectActionTypes.SET_HEIGHT,
    payload: newHeight,
  });

  console.debug("SmartObject added to page:", {
    assetBlobPath: blobPath,
    assetVersionId,
  });
}
